"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  Home,
  PlusCircle,
  User,
  MessageCircle,
} from "lucide-react";
import { CommerceIcons } from "@/lib/icons/commerce-icons";
import { useCallback, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useSelectionMode } from "@/components/context/selection-mode-context";
import { motion } from "framer-motion";
import { usePanelIs, useOverlay } from "@/components/context/overlay-context";
import { consumeAccountHubReopen } from "@/lib/account-hub-reopen";
import { useCartContext } from "@/components/cart/cart-provider";
import { useCartCalculations } from "@/hooks/use-cart-calculations";
import { useOfferActionCount } from "@/hooks/use-offer";
import { useMobile } from "@/hooks/use-mobile";
import { ProfileTabSlider } from "@/components/UserUI/Navigation/profile-tab-slider";
import {
  deriveHideNavBar,
  deriveActiveSection,
  deriveGuestActiveSection,
  type NavSection,
} from "@/lib/nav/derive-nav-visibility";

import type { PaymentStatus } from "@/components/settings/payment-status-pill";

interface BottomNavBarClientProps {
  user: any;
  className?: string;
  paymentStatus: PaymentStatus;
}

export default function BottomNavBarClient({
  user,
  className,
  paymentStatus,
}: BottomNavBarClientProps) {
  const router = useRouter();
  const { cartItems } = useCartContext();
  const { uniqueItemCount } = useCartCalculations(cartItems);
  const offerActionCount = useOfferActionCount();
  const profileUsername =
    typeof user?.username === "string" && user.username.trim() ? user.username.trim() : null;
  const metadataUsername =
    typeof user?.user_metadata?.username === "string" && user.user_metadata.username.trim()
      ? user.user_metadata.username.trim()
      : null;
  const myUsername = profileUsername ?? metadataUsername;
  const myProfileHref = myUsername ? `/${encodeURIComponent(myUsername)}` : "/profile";
  const { overlay, openPanel, closePanel, openUtility } = useOverlay();
  const isProfileTabOpen = usePanelIs("profile");
  // Spec §E 1.5: snap-feed details is ONE panel kind for both the mobile
  // bottom sheet and the desktop 420px side panel (lib/overlay/overlay-state.ts
  // can't distinguish which presentation opened it — one canonical kind, by
  // design). Pre-migration this nav bar only ever observed the MOBILE dataset
  // flag (`data-feed-details-open`) — the desktop panel wrote a SEPARATE key
  // (`data-feed-desktop-details-open`) that nothing here read. Reproduce that
  // exactly by feeding `useMobile()` (the same breakpoint hook the two
  // producers already use) into `deriveHideNavBar` below, rather than growing
  // a presentation axis on the FSM itself.
  const isMobileViewport = useMobile();
  // Adapter over openPanel/closePanel — ProfileTabSlider is controlled via a
  // boolean open/onOpenChangeAction pair (its own internal handlers call
  // `onOpenChangeAction(false)`), so this keeps that shape while the actual
  // source of truth is the shared overlay panel slot (§C.1).
  const setIsProfileTabOpen = useCallback(
    (open: boolean) => {
      if (open) openPanel({ kind: "profile" });
      else closePanel();
    },
    [openPanel, closePanel],
  );
  const pathname = usePathname();

  const [guestClickedSection, setGuestClickedSection] = useState<"home" | null>(null);
  const [connectedClickedSection, setConnectedClickedSection] = useState<NavSection | null>(null);

  const activeSection = deriveActiveSection({
    overlay,
    pathname,
    myUsername,
    clickedSection: connectedClickedSection,
  });

  const guestActiveSection = deriveGuestActiveSection({
    pathname,
    clickedSection: guestClickedSection,
  });

  useEffect(() => {
    setGuestClickedSection(null);
    setConnectedClickedSection(null);
  }, [pathname]);

  useEffect(() => {
    if (consumeAccountHubReopen()) {
      setIsProfileTabOpen(true);
    }
  }, [pathname, setIsProfileTabOpen]);

  // Lock the bottom safe-area inset to the largest value the browser reports.
  // The inset toggles between 0 and the device home-indicator height depending
  // on the browser toolbar (hidden on the fixed feed, shown on body-scrolling
  // pages like settings). Reading env() live makes the nav change height on
  // navigation; pinning the max keeps it constant everywhere.
  useEffect(() => {
    const root = document.documentElement;
    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:fixed;left:0;bottom:0;width:0;height:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none;";
    document.body.appendChild(probe);

    let maxInset = 0;
    const sync = () => {
      const inset = probe.getBoundingClientRect().height;
      if (inset > maxInset + 0.5) {
        maxInset = inset;
        root.style.setProperty("--kraahl-safe-bottom", `${inset}px`);
      }
    };
    const reset = () => {
      maxInset = 0;
      root.style.removeProperty("--kraahl-safe-bottom");
      requestAnimationFrame(sync);
    };

    sync();
    const raf = requestAnimationFrame(sync);
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", reset);
    window.visualViewport?.addEventListener("resize", sync);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", reset);
      window.visualViewport?.removeEventListener("resize", sync);
      probe.remove();
    };
  }, []);

  let isSelectionModeActive = false;
  try {
    const selectionMode = useSelectionMode();
    isSelectionModeActive = selectionMode.isSelectionModeActive;
  } catch (e) {
  }

  const hideNavBar = deriveHideNavBar({
    overlay,
    pathname,
    isMobileViewport,
    isSelectionModeActive,
  });

  if (hideNavBar && !isProfileTabOpen) {
    return null;
  }

  return (
    <>
      {user ? (
        <ProfileTabSlider
          open={isProfileTabOpen}
          onOpenChangeAction={setIsProfileTabOpen}
          user={user}
          myProfileHref={myProfileHref}
          offerActionCount={offerActionCount}
          paymentStatus={paymentStatus}
        />
      ) : null}

      {!hideNavBar ? (
        <nav
          className={cn(
            "fixed bottom-0 inset-x-0 z-50 md:hidden pointer-events-none",
            className,
          )}
          data-nav-bar
        >
          <div className="pointer-events-auto mx-auto mb-[calc(0.65rem+var(--kraahl-safe-bottom))] h-14 w-[calc(100%-1.5rem)] max-w-sm overflow-hidden rounded-full border border-border/60 bg-background/70 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.35)] backdrop-blur-2xl">
            {user ? (
              <div className="w-full h-full flex items-center justify-around px-2">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  onClick={() => {
                    setConnectedClickedSection("home");
                    router.push("/feed");
                  }}
                >
                  <Home
                    className={cn(
                      "h-6 w-6 transition-colors",
                      activeSection === "home"
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                    strokeWidth={activeSection === "home" ? 2.5 : 2}
                  />
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  aria-label="Messages"
                  onClick={() => router.push("/chat")}
                >
                  <MessageCircle
                    className="h-6 w-6 text-muted-foreground transition-colors"
                    strokeWidth={2}
                  />
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.85 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  onClick={() => openPanel({ kind: "creation" })}
                  aria-label="Create listing"
                  data-testid="create-listing-trigger"
                >
                  <div className="h-8 w-8 rounded-full bg-foreground flex items-center justify-center">
                    <PlusCircle className="h-5 w-5 text-background" strokeWidth={2.5} />
                  </div>
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="relative flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  onClick={() => {
                    setIsProfileTabOpen(!isProfileTabOpen);
                  }}
                >
                  <User
                    className={cn(
                      "h-6 w-6 transition-colors",
                      activeSection === "profile"
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                    strokeWidth={activeSection === "profile" ? 2.5 : 2}
                  />
                  {offerActionCount > 0 && (
                    <span className="absolute top-1 right-1/2 translate-x-3 inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-semibold leading-none text-white bg-red-600 rounded-full">
                      {offerActionCount > 99 ? "99+" : offerActionCount}
                    </span>
                  )}
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="relative flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  data-testid="cart-trigger"
                  onClick={() => openUtility({ kind: "cart" })}
                >
                  <CommerceIcons.addToCart
                    className="h-6 w-6 text-muted-foreground transition-colors"
                    strokeWidth={2}
                  />
                  {uniqueItemCount > 0 && (
                    <span className="absolute top-1 right-1/2 translate-x-3 inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-semibold leading-none text-white bg-red-600 rounded-full">
                      {uniqueItemCount > 99 ? "99+" : uniqueItemCount}
                    </span>
                  )}
                </motion.button>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-around px-2">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  onClick={() => {
                    setGuestClickedSection("home");
                    router.push("/feed");
                  }}
                >
                  <Home
                    className={cn(
                      "h-6 w-6 transition-colors",
                      guestActiveSection === "home"
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                    strokeWidth={guestActiveSection === "home" ? 2.5 : 2}
                  />
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  className="relative flex-1 flex flex-col items-center justify-center gap-1 h-full"
                  data-testid="cart-trigger"
                  onClick={() => openUtility({ kind: "cart" })}
                >
                  <CommerceIcons.addToCart
                    className="h-6 w-6 text-muted-foreground transition-colors"
                    strokeWidth={2}
                  />
                  {uniqueItemCount > 0 && (
                    <span className="absolute top-1 right-1/2 translate-x-3 inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-semibold leading-none text-white bg-red-600 rounded-full">
                      {uniqueItemCount > 99 ? "99+" : uniqueItemCount}
                    </span>
                  )}
                </motion.button>
              </div>
            )}
          </div>
        </nav>
      ) : null}
    </>
  );
}
